/**
 * Utility functions for PrintingGraphs component
 * Parses print-photo event data into graph-ready format
 */

import { Event } from '../../types';

const PRECISION = 1;

export interface FeederInfo {
  name: string;
  index: number;
  temperature: number;
  temperature2: number;
  gcodePosition: number;
  cncE: number;
  z: number;
  feederE: number;
  feederP: number;
  cncEvsFeederP: number;
  devPercentage: number;
  difGcodePositionFeederP: number;
  gCodeOffset: number | null | undefined;
  klipperGCodeOffset: number | null | undefined;
  gCodeOffsetManualToAuto: number | null | undefined;
  gCodeOffsetAutoToManual: number | null | undefined;
}

function parseEventInfoField(value: number): number {
  return isNaN(+value.toFixed(PRECISION)) ? 0 : +value.toFixed(PRECISION);
}

export const getFeederInfo = (events: Event[]): FeederInfo[] => {
  const feederInfo: FeederInfo[] = [];

  events.forEach((e, index) => {
    let parsedFields;
    try {
      parsedFields = JSON.parse(e.rawContent)?.fields;
    } catch {
      return;
    }

    if (!parsedFields) return;

    // Safely extract extruderOffset with optional chaining
    const extruderOffset = +(parsedFields?.cnc?.position?.extruderOffset ?? 0);

    const temperature = parseEventInfoField(+parsedFields?.cnc?.temperature?.T0);
    const temperature2 = parseEventInfoField(+parsedFields?.cnc?.temperature?.T1);
    const gcodePosition = parseEventInfoField(+parsedFields?.cnc?.gcodePosition);
    const cncE = parseEventInfoField(+parsedFields?.cnc?.position?.E);
    const z = parseEventInfoField(+parsedFields?.cnc?.position?.Z);
    const feederE = parseEventInfoField(+parsedFields?.diagnostics?.feeder?.E + extruderOffset);
    const feederP = parseEventInfoField(+parsedFields?.diagnostics?.feeder?.P + extruderOffset);
    const cncEvsFeederP = parseEventInfoField(+parsedFields?.difCncEFeederP);
    const devPercentage = parseEventInfoField(+parsedFields?.coefDeviation);
    const difGcodePositionFeederP = parseEventInfoField(+parsedFields?.difGcodePositionFeederP);
    let gCodeOffset: number | null = parseEventInfoField(+parsedFields?.gCodeOffset);
    let klipperGCodeOffset: number | null =
      typeof parsedFields?.klipperConStatus?.gCodeOffset === 'string'
        ? parseEventInfoField(+parsedFields.klipperConStatus.gCodeOffset.split('=').join(''))
        : gCodeOffset;

    let gCodeOffsetManualToAuto = null;
    let gCodeOffsetAutoToManual = null;

    if (gCodeOffset === klipperGCodeOffset) {
      klipperGCodeOffset = null;
      if (index > 0) {
        const { klipperGCodeOffset: prevKlipperGCodeOffset } = feederInfo[index - 1];
        if (prevKlipperGCodeOffset !== null) {
          gCodeOffsetAutoToManual = gCodeOffset;
          feederInfo[index - 1].gCodeOffsetAutoToManual = prevKlipperGCodeOffset;
        }
      }
    } else {
      gCodeOffset = null;
      if (index > 0) {
        const { gCodeOffset: prevGCodeOffset } = feederInfo[index - 1];
        if (prevGCodeOffset !== null) {
          gCodeOffsetManualToAuto = klipperGCodeOffset;
          feederInfo[index - 1].gCodeOffsetManualToAuto = prevGCodeOffset;
        }
      }
    }

    feederInfo.push({
      name: 'Events',
      index,
      temperature,
      temperature2,
      gcodePosition,
      cncE,
      z,
      feederE,
      feederP,
      cncEvsFeederP,
      devPercentage,
      difGcodePositionFeederP,
      gCodeOffset,
      klipperGCodeOffset,
      gCodeOffsetManualToAuto,
      gCodeOffsetAutoToManual,
    });
  });

  return feederInfo;
};
